import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  X, ChevronLeft, ChevronRight, Play, Pause, RotateCcw, Volume2, Check, ListChecks,
} from "lucide-react";
import "./cooking.css";

const pad = (n) => String(n).padStart(2, "0");
const fmt = (sec) => `${Math.floor(sec / 60)}:${pad(sec % 60)}`;

function beep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    [0, 0.35, 0.7].forEach((t) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 880;
      gain.gain.value = 0.15;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + t);
      osc.stop(ctx.currentTime + t + 0.2);
    });
    setTimeout(() => ctx.close(), 1500);
  } catch {
    /* audio not available */
  }
  navigator.vibrate?.([200, 100, 200]);
}

/**
 * Full-screen, one-step-at-a-time cooking view.
 * Keeps the screen awake, supports arrow keys / swipe, a per-step timer and read-aloud.
 */
const CookingMode = ({ open, onClose, title, steps, ingredients, onSpeak, onStopSpeak }) => {
  const [index, setIndex] = useState(0);
  const [showIngredients, setShowIngredients] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const [running, setRunning] = useState(false);
  const touchX = useRef(null);

  const step = steps[index];
  const last = index === steps.length - 1;

  const go = useCallback(
    (delta) => setIndex((i) => Math.min(Math.max(i + delta, 0), steps.length - 1)),
    [steps.length]
  );

  // Reset when opened
  useEffect(() => {
    if (open) {
      setIndex(0);
      setShowIngredients(false);
    }
  }, [open]);

  // Each step starts with a fresh timer
  useEffect(() => {
    setRunning(false);
    setRemaining(step?.minutes ? step.minutes * 60 : null);
  }, [index, step?.minutes, open]);

  // Countdown
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(id);
          setRunning(false);
          beep();
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [running]);

  // Keyboard
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, go, onClose]);

  // Keep the screen awake while cooking
  useEffect(() => {
    if (!open || !("wakeLock" in navigator)) return undefined;
    let lock = null;
    let cancelled = false;
    const acquire = async () => {
      try {
        lock = await navigator.wakeLock.request("screen");
        if (cancelled) lock.release();
      } catch {
        /* denied or unsupported */
      }
    };
    const onVisible = () => document.visibilityState === "visible" && acquire();
    acquire();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      lock?.release?.().catch(() => {});
    };
  }, [open]);

  if (!open || steps.length === 0) return null;

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 60) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  return (
    <div
      className="cook"
      role="dialog"
      aria-modal="true"
      aria-label={`Cooking mode: ${title}`}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <header className="cook-top">
        <div className="cook-title">
          <span>Cooking</span>
          <strong>{title}</strong>
        </div>
        <button
          className={`cook-chip ${showIngredients ? "on" : ""}`}
          onClick={() => setShowIngredients((v) => !v)}
          aria-pressed={showIngredients}
        >
          <ListChecks size={16} /> Ingredients
        </button>
        <button className="cook-close" onClick={() => { onStopSpeak?.(); onClose(); }} aria-label="Exit cooking mode">
          <X size={22} />
        </button>
      </header>

      <div
        className="cook-progress"
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={steps.length}
        aria-valuenow={index + 1}
      >
        <span style={{ width: `${((index + 1) / steps.length) * 100}%` }} />
      </div>

      <main className="cook-main">
        <div className="cook-step" key={index}>
          <span className="cook-count">
            Step {index + 1} <em>of {steps.length}</em>
          </span>
          <p className="cook-text">{step.text}</p>

          <div className="cook-tools">
            {remaining !== null && (
              <div className={`cook-timer ${remaining === 0 ? "done" : ""}`}>
                <span className="cook-time" aria-live="off">{fmt(remaining)}</span>
                <button
                  onClick={() => (remaining === 0 ? setRemaining(step.minutes * 60) : setRunning((r) => !r))}
                  aria-label={running ? "Pause timer" : "Start timer"}
                >
                  {running ? <Pause size={18} /> : <Play size={18} />}
                </button>
                <button
                  onClick={() => { setRunning(false); setRemaining(step.minutes * 60); }}
                  aria-label="Reset timer"
                >
                  <RotateCcw size={16} />
                </button>
              </div>
            )}
            <button className="cook-chip" onClick={() => onSpeak?.(step.text)}>
              <Volume2 size={16} /> Read aloud
            </button>
          </div>
        </div>

        {showIngredients && (
          <aside className="cook-ingredients" aria-label="Ingredients">
            <h3>Ingredients</h3>
            <ul>
              {ingredients.map((ing, i) => (
                <li key={i}>{ing}</li>
              ))}
            </ul>
          </aside>
        )}
      </main>

      <footer className="cook-nav">
        <button className="cook-btn" onClick={() => go(-1)} disabled={index === 0}>
          <ChevronLeft size={20} /> Back
        </button>
        {last ? (
          <button className="cook-btn primary" onClick={() => { onStopSpeak?.(); onClose(); }}>
            <Check size={20} /> Done
          </button>
        ) : (
          <button className="cook-btn primary" onClick={() => go(1)}>
            Next <ChevronRight size={20} />
          </button>
        )}
      </footer>
    </div>
  );
};

export default CookingMode;
